import Dropdown from "@components/form/Dropdown.component";
import PipelineNodeDataTable from "@components/pipelineNodes/PIpelineNodeDataTable";
import { DraftOnly, ProdOnly } from "@components/project/DraftModeRequired.component";
import { PipelineNode, PipelineNodeField, PipelineNodeORM } from "@models/pipelineNode";
import { BrokenWords, Pane, PaneContent } from "@pages/PageStructure.component";
import { requireConfirmation} from "@services/alert/alert.service";
import { getErrorMessage } from "@services/errors.service";
import toast from "@services/toast.service";
import { enterDraftMode, invalidateMissionControlDataFlowData, invalidatePipelineNodes, useIsInDraftMode, usePipelineNode } from "@stores/data.store";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form, Modal, Dropdown as BSDropdown, ButtonGroup, Button } from "react-bootstrap";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import Editor from "@monaco-editor/react";
import { Allotment, AllotmentHandle } from "allotment";
import styled from 'styled-components';
import { Updater, useImmer } from "use-immer";
import { useRouteBlocker } from "@services/routing.service";
import { ApiError } from "@services/api/api.service";
import PliableLoader from "@components/loaders/PliableLoader.component";
import PipelineNodeSelector from "@components/pipelineNodes/PipelineNodeSelector.component";
import PipelineNodeColumnSelector, { PipelineNodeMultiColumnSelector } from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import SaveButton from "@components/button/SaveButton.component";
import PipelineNodeNotFound from "@components/pipelineNodes/PipelineNodeNotFound.component";
import PipelineNodeReportEditor from "@components/pipelineNodes/PipelineNodeReportEditor";
import PipelineNodeReportBuilder from "@components/pipelineNodes/PipelineNodeReportBuilder.component";
import PipelineNodeMappingConfigForm from "@components/pipelineNodes/PipelineNodeMappingConfigForm.component";
import PipelineNodeBasicConfiguration from "@components/pipelineNodes/configuration/PipelineNodeBasicConfiguration.component";
import PipelineNodeWhitelistConfiguration from "@components/pipelineNodes/configuration/PipelineNodeWhitelistConfiguration.component";
import PipelineNodeOutputConfiguration from "@components/pipelineNodes/configuration/PipelineNodeOutputConfiguration.component";
import PipelineNodeColumnOrder from "@components/pipelineNodes/PipelineNodeColumnOrder.component";
import Danger from "@components/statusIndicators/Danger.component";
import Warning from "@components/statusIndicators/Warning.component";
import FullScreenTakeover from "@components/general/FullScreenTakeover.component";
import BuildNodeModal from "@components/pipelineNodes/modal/BuildNodeModal.component";
import { triggerBuild } from "@services/alert/BuildModal.component";
import PipelineNodeDataMartConfiguration from "@components/pipelineNodes/configuration/PipelineNodeDataMartConfiguration.component";
import ConfigurationSection from "@components/pipelineNodes/configuration/ConfigurationSection.component";
import SourceTableConfig from "@components/pipelineNodes/configuration/SourceTableConfig.component";
import DefaultSortForm from "@pages/SourceRecordType/DefaultSortForm.component";

export const DataPaneHeader = styled.div`
background: var(--ct-light);
height: 30px;
line-height: 30px;
padding: 0px 1rem;
display: flex;
flex-direction: row;
cursor: pointer;
border-bottom: 1px solid var(--ct-border-color);

&:hover {
    background-color: var(--ct-border-color);
}

h2 {
    margin: 0px;
    padding: 0px;
    line-height: 30px;
    font-weight: 200;
    font-size: 16px;
    flex: 1;
}

button {
    border: none;
    background: none;
    padding: 0px;
    margin: 0px;
    font-size: 24px;
    font-weight: bold;
    color: var(--ct-text-muted);

    &:hover {
        color: black;
        cursor: pointer;
    }
}
`

interface NodeConfigProps {
    node: PipelineNode;
    onChange: Updater<PipelineNode>;
}

const ViewConfig = (props: NodeConfigProps) => {
    const inDraftMode = useIsInDraftMode();
    const upstreamNode = usePipelineNode(props.node.upstream_node_ids[0]);
    return <div>
        <PipelineNodeBasicConfiguration node={props.node} onChange={props.onChange}/>
        <hr />
        <h2>View Settings</h2>
        <Form.Group className="mb-3">
            <Form.Label>Upstream Node</Form.Label>
            <PipelineNodeSelector
                selectedId={props.node.upstream_node_ids.length > 0 ? props.node.upstream_node_ids[0] : ''}
                onSelect={(selected: PipelineNode | undefined) => {
                    props.onChange(draft => {
                        if (selected) {
                            draft.upstream_node_ids = [selected.id as string]
                        } else {
                            draft.upstream_node_ids = [];
                        }
                        draft.fields = [];
                    });
                }}
                
            />

        </Form.Group>
        {upstreamNode.isLoading && <i className="mdi mdi-loading mdi-spin"></i>}
        {upstreamNode.data && <>
            <Form.Group className="mb-3">
                <Form.Label>Select Column(s)</Form.Label>
                <PipelineNodeMultiColumnSelector
                    pipelineNodeId={upstreamNode.data.id as string}
                    onSelect={(fieldIds: string[]) => {
                        props.onChange(draft => {
                            draft.upstream_column_ids = fieldIds;
                        })
                    }}
                    selectedIds={props.node.upstream_column_ids || []}
                    disabled={!inDraftMode}
                />

            </Form.Group>
            {upstreamNode.data.include_in_snapshots && <Form.Group className="mb-3">
                <Form.Check
                    checked={props.node.include_historical_data}
                    label="Include historical snapshot data"
                    onChange={() => {
                        props.onChange(draft => {
                            draft.include_historical_data = !draft.include_historical_data;
                        })
                    }}
                />
                {inDraftMode && <Form.Text>
                    Note that snapshots do not run on a schedule in develop mode, so you may have gaps in your data. In production mode a snapshot is taken after every scheduled build.
                </Form.Text>}
            </Form.Group>}
            
            
        </>}
        <hr />
        <PipelineNodeWhitelistConfiguration node={props.node} onChange={props.onChange}/>
        <div className="mb-5"></div>
    </div>
}

const DateDimConfig = (props: NodeConfigProps) => {
    const inDraftMode = useIsInDraftMode();
    return <div>
        {/* <Form.Group className="mb-3">
            <Form.Label>Fiscal Year Start</Form.Label>
            <div className="row">
                <div className="col-6 pe-1">
                    <Form.Group>
                        <Form.Label className="small">Month</Form.Label>
                        <select className="form-control" value={props.node.special_node_args?.fiscal_year_month} onChange={(e) => {
                            props.onChange(draft => {
                                if (!draft.special_node_args) {
                                    draft.special_node_args = {};
                                }
                                draft.special_node_args.fiscal_year_month = e.target.value;
                            })
                        }}>
                            <option value="01">January</option>
                            <option value="02">February</option>
                            <option value="03">March</option>
                            <option value="04">April</option>
                            <option value="05">May</option>
                            <option value="06">June</option>
                            <option value="07">July</option>
                            <option value="08">August</option>
                            <option value="09">September</option>
                            <option value="10">October</option>
                            <option value="11">November</option>
                            <option value="12">December</option>
                        </select>
                    </Form.Group>
                </div>
                <div className="col-6 ps-1">
                    <Form.Group>
                        <Form.Label className="small">Day</Form.Label>
                        <input type="number" className="form-control" min="1" max="31" value={props.node.special_node_args?.fiscal_year_day} onChange={(e) => {
                            props.onChange(draft => {
                                if (!draft.special_node_args) {
                                    draft.special_node_args = {};
                                }
                                draft.special_node_args.fiscal_year_day = e.target.value;
                            })
                        }}/>
                        
                    </Form.Group>
                </div>
            </div>
            
        </Form.Group> */}
        <ConfigurationSection>
            <PipelineNodeBasicConfiguration node={props.node} onChange={props.onChange}/>

        </ConfigurationSection>
        <ConfigurationSection>
            <h2><i className="mdi mdi-calendar"></i> Calendar Configuration</h2>
            <Form.Group className="mb-3">
                <Form.Label>Date Range</Form.Label>
                <div className={`row ${!inDraftMode ? 'disabled' : ''}`}>
                    <div className="col-6 pe-1">
                        <Form.Group>
                            <Form.Label className="small">Start</Form.Label>
                            <Form.Control type="date" value={props.node.special_node_args?.start_date} onChange={(e) => {
                                props.onChange(draft => {
                                    if (!draft.special_node_args) {
                                        draft.special_node_args = {};
                                    }
                                    draft.special_node_args.start_date = e.target.value;
                                })
                            }} />
                        </Form.Group>
                        
                    </div>
                    <div className="col-6 ps-1">
                        <Form.Group>
                            <Form.Label className="small">End</Form.Label>
                            <Form.Control type="date" value={props.node.special_node_args?.end_date} onChange={(e) => {
                                props.onChange(draft => {
                                    if (!draft.special_node_args) {
                                        draft.special_node_args = {};
                                    }
                                    draft.special_node_args.end_date = e.target.value;
                                })
                            }} />
                        </Form.Group>
                    </div>
                </div>
                <Form.Text>Defaults to <code>1/1/2020</code> - <code>TODAY</code> if empty</Form.Text>

                
            </Form.Group>
        </ConfigurationSection>
        
    </div>
}

const SplitConfig = (props: NodeConfigProps) => {
    const inDraftMode = useIsInDraftMode();
    return <>
        <ConfigurationSection>
            <PipelineNodeBasicConfiguration node={props.node} onChange={props.onChange}/>

        </ConfigurationSection>
        <ConfigurationSection>

            <h2><i className="mdi mdi-set-split"></i> Split Configuration</h2>
            <Form.Group className="mb-3">
                <Form.Label>Select source model</Form.Label>
                <PipelineNodeSelector
                    disabled={!inDraftMode}
                    selectedId={props.node.upstream_node_ids.length > 0 ? props.node.upstream_node_ids[0] : ''}
                    onSelect={(selected: PipelineNode | undefined) => {
                        if (selected) {
                            props.onChange(draft => {
                                draft.upstream_node_ids = [selected.id as string];
                            })
                        } else {
                            props.onChange(draft => {
                                draft.upstream_node_ids = [];
                            })
                        }
                    }}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Select source model column to split on</Form.Label>
                <PipelineNodeColumnSelector
                    disabled={!inDraftMode}
                    pipelineNodeId={props.node.upstream_node_ids.length > 0 ? props.node.upstream_node_ids[0] : ''}
                    selectedId={props.node.special_node_args?.column_id}
                    onSelect={(selected: PipelineNodeField|undefined) => {
                        props.onChange(draft => {
                            if (!draft.special_node_args) {
                                draft.special_node_args = {};
                            }
                            draft.special_node_args.column_id = selected?.id || '';
                        });
                    }}
                />

            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Column Format</Form.Label>
                <Dropdown
                    disabled={!inDraftMode}
                    selected={props.node.special_node_args?.column_format}
                    onChange={(newVal: string) => {
                        props.onChange(draft => {
                            if (!draft.special_node_args) {
                                draft.special_node_args = {};
                            }
                            draft.special_node_args.column_format = newVal;
                        });
                    }}
                    options={[
                        {
                            label: 'Delimited String',
                            value: 'DELIMITED_STRING',
                        }, {
                            label: 'JSON Array',
                            value: 'ARRAY',
                        }
                    ]}
                />
            </Form.Group>
            <Form.Group>
                <Form.Label>Delimiter</Form.Label>
                <Form.Control disabled={!inDraftMode || (props.node.special_node_args?.column_format != 'DELIMITED_STRING')} value={props.node.special_node_args?.delimiter} onChange={(e) => {
                    props.onChange(draft => {
                        draft.special_node_args!.delimiter = e.target.value;
                    })
                }}
                />
            </Form.Group>
        </ConfigurationSection>

        <ConfigurationSection>
            <PipelineNodeWhitelistConfiguration node={props.node} onChange={props.onChange}/>

        </ConfigurationSection>
        <ConfigurationSection>
            <PipelineNodeOutputConfiguration node={props.node} onChange={props.onChange}/>

        </ConfigurationSection>
    </>
}


const CustomSQLConfig = (props: NodeConfigProps) => {
    const editorRef = useRef<any>();
    const inDraftMode = useIsInDraftMode();
    const [showPliableColumnModal, setShowPliableColumnModel] = useState(false);
    const [completionDisposable, setCompletionDisposable] = useState<any>();

    useEffect(() => {
        if (props.node.id && !props.node.ignore_pliable_columns_in_custom_sql && !!props.node.needs_pliable_columns_in_custom_sql && inDraftMode) {
           
            setShowPliableColumnModel(true);
        }
    }, [props.node.ignore_pliable_columns_in_custom_sql, props.node.needs_pliable_columns_in_custom_sql, inDraftMode])
    
    const editorDidMount = useCallback((editor: any, monacoParam: any) => {
        editorRef.current = editor;

        // setCompletionDisposable(
        //     monacoParam.languages.registerCompletionItemProvider('*', {
        //         provideCompletionItems: function(model: any, position: any) {

        //             // add BO objects
        //             const suggestions: any[] = props.otherFieldsForAutocomplete.map(f => {
        //                 return {
        //                     label: f,
        //                     kind: monacoParam.languages.CompletionItemKind.Variable,
        //                     insertText: `"${f}"`

        //                 }
        //             })
    

        //             return {
        //                 suggestions: suggestions
        //             };
        //         }
        //     })
        // );
    }, []);

    const addColumns = useCallback(() => {
        props.onChange(draft => {
            const sql = draft.custom_sql || '';
            const trimmedSql = sql.trim();
            if (!/^with\s+original_model\s+as\s*\(/i.test(trimmedSql)) {
                draft.custom_sql = 'with original_model as (\n' + sql + `\n)\n\nselect *, ('plb:::m:${draft.name}::r:' || sha1(hash(*)::string)) as _plb_uuid, current_timestamp() as _plb_loaded_at from original_model`;
            }
            draft.needs_pliable_columns_in_custom_sql = false;
        });
        setShowPliableColumnModel(false);
    }, [props.onChange]);

    const ignore = useCallback(() => {
        props.onChange(draft => {
            draft.ignore_pliable_columns_in_custom_sql = true;
        });
        setShowPliableColumnModel(false);
    }, [props.onChange]);

    const [showFullScreen, setShowFullScreen] = useState(false);
    return <>
        <Modal show={showPliableColumnModal} onHide={() => setShowPliableColumnModel(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Warning</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                This node needs a unique ID column called <code>_plb_uuid</code> and a timestamp column called <code>_plb_loaded_at</code> in order to be used in other Pliable models. Do you want to automatically add those columns?
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-light me-1" onClick={ignore}>Ignore</button>
                <button className="btn btn-pliable" onClick={addColumns}>Add Columns</button>
            </Modal.Footer>
        </Modal>
        <FullScreenTakeover show={showFullScreen} onHide={() => setShowFullScreen(false)}>
            {showFullScreen && <Editor
                onChange={(value) => props.onChange(draft => {
                    draft.custom_sql = value ? value as string : '';
                })}
                height="100%"
                defaultLanguage="sql"
                options={{
                    readOnly: !inDraftMode,
                    minimap: { enabled: false },
                }}
                value={props.node.custom_sql}
                onMount={(editor, monaco) => editorDidMount(editor, monaco)}
            />}
        </FullScreenTakeover>
        {props.node.has_duplicate_plb_uuids && (
            <div className="mb-3">
                <Warning>
                    <div>
                        <strong>Duplicate PLB_UUIDs Detected</strong><br />
                        This custom DBT model has duplicate _PLB_UUID values, which may cause ambiguous relationships in downstream processes. Please ensure each record has a unique _PLB_UUID.
                    </div>
                </Warning>
            </div>
        )}
        <ConfigurationSection>
            <div>
                <PipelineNodeBasicConfiguration node={props.node} onChange={props.onChange}/>

            </div>
        </ConfigurationSection>
        
        <ConfigurationSection>
            
            <div >
                <div className="d-flex center-vertically mb-2">
                    <h2 className="mb-0 flex-1">
                        <i className="mdi mdi-code-braces"></i> Custom SQL
                        
                    </h2>
                    <button className="icon-button font-24 float-right" title="Fullscreen" onClick={() => {
                        setShowFullScreen(true);
                    }}>
                        <i className="mdi mdi-fullscreen"></i>
                    </button>
                </div>
                
                <Editor
                    onChange={(value) => props.onChange(draft => {
                        draft.custom_sql = value ? value as string : '';
                    })}
                    height="500px"
                    defaultLanguage="sql"
                    options={{
                        readOnly: !inDraftMode,
                        minimap: { enabled: false },
                    }}
                    value={props.node.custom_sql}
                    onMount={(editor, monaco) => editorDidMount(editor, monaco)}
                />
            </div>
        </ConfigurationSection>
        
        <ConfigurationSection>
            <div>
                <h2 className="mb-3">
                    <i className="mdi mdi-sort"></i> Default Sort Order
                </h2>
                <Form.Group className="mb-3">
                    <Form.Label>Sort Order</Form.Label>
                    <DefaultSortForm
                        fields={props.node.fields}
                        currentSort={props.node.default_sort || []}
                        onChange={(sort) => {
                            props.onChange(draft => {
                                draft.default_sort = sort;
                            });
                        }}
                        disabled={!inDraftMode}
                    />
                    <Form.Text className="text-muted">
                        Configure the default sort order for this node's output.
                    </Form.Text>
                </Form.Group>
            </div>
        </ConfigurationSection>
    </>
}

const PipelineNodeConfigurationPage = () => {
    const { pipelineNodeId } = useParams();
    const pipelineNode = usePipelineNode(pipelineNodeId as string);

    const allotmentRef = useRef<AllotmentHandle>(null);
    const [dataViewerIsOpen, setDataViewerIsOpen] = useState(false);
    const isInDraft = useIsInDraftMode();

    const [showSql, setShowSql] = useState(false);


    const [editingNode, setEditingNode] = useImmer<PipelineNode>({
        id: null,
        name: '',
        label: '',
        description: '',
        table_name: '',
        upstream_node_ids: [],
        fields: [],
        node_type: '',
        flat_file: false
    });
    
    const onEditNodeChange = useCallback((n: any) => {
        setEditingNode(n);

        // Weird change, haven't figured out why this was happening,
        // but there was an infinite loop in dev that this somehow fixed.
        setTimeout(() => {
            setPageDirty(true);
        })
        
    }, [pipelineNode.dataUpdatedAt]);


    useEffect(() => {
        if (pipelineNode.data) {
            setEditingNode(pipelineNode.data);
            setPageDirty(false);
        }
    }, [pipelineNode.dataUpdatedAt]);

    const allotmentResized = useDebouncedCallback((newSizes: number[]) => {
        if (newSizes[1] === 30) {
            setDataViewerIsOpen(false);
        } else {
            setDataViewerIsOpen(true);
        }
    }, 250);

    const openDataViewer = useCallback(() => {
        if (!allotmentRef.current) {
            return;
        }
        allotmentRef.current.resize([1000, 1000]);
    }, [allotmentRef]);

    const toggleDataViewer = useCallback(() => {
        if (!allotmentRef.current) {
            return;
        }

        if (dataViewerIsOpen) {
            // There's a resize issue with the editor so we need to switch back to table mode first
            setShowSql(false);
            window.requestAnimationFrame(() => {
                allotmentRef.current!.reset();

            })
        } else {
            allotmentRef.current!.resize([1000, 1000]);

        }
        
    }, [allotmentRef, dataViewerIsOpen]);


    const [saving, setSaving] = useState(false);
    const save = useCallback(async () => {
        setSaving(true);
        try {
            await PipelineNodeORM.save(editingNode);
            invalidatePipelineNodes();
            invalidateMissionControlDataFlowData();
            
            setPageDirty(false);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setSaving(false);
        }
        
    }, [pipelineNode.dataUpdatedAt, editingNode]);

    const { pageDirty, setPageDirty } = useRouteBlocker(save);

    // Register save function with parent component via outlet context
    const outletContext = useOutletContext<{
        registerSave: (saveFn: () => Promise<void>) => void;
        unregisterSave: () => void;
        setPageDirty: (dirty: boolean) => void;
    }>();

    useEffect(() => {
        if (outletContext?.registerSave) {
            outletContext.registerSave(save);
        }
        return () => {
            if (outletContext?.unregisterSave) {
                outletContext.unregisterSave();
            }
        };
    }, [save, outletContext]);

    // Update parent's pageDirty state whenever it changes
    useEffect(() => {
        if (outletContext?.setPageDirty) {
            outletContext.setPageDirty(pageDirty);
        }
    }, [pageDirty, outletContext]);

    const onChange = useCallback((attr: keyof PipelineNode, newVal: any) => {
        setEditingNode(draft => {
            // @ts-ignore
            draft[attr] = newVal;
        })
        // setEditingNode(draft => {
        //     // @ts-ignore
        //     draft[attr] = newVal;
        // });
    }, [editingNode, setEditingNode, setPageDirty]);

   


    useEffect(() => {
        // Switch back to table view if they change the node ID
        setShowSql(false);
    }, [pipelineNodeId]);

    const topPane = <div style={{padding: '2.25rem 2.25rem 0 2.25rem', height: '100%', overflowY: 'scroll'}}>
        {editingNode && editingNode.build_error && <div className="mb-2">
            <Danger hideIcon>
            <div className="w-100">
                    <h4>Build Error</h4>
                    <BrokenWords className="font-13 font-code">
                        {editingNode.build_error}
                    </BrokenWords>
                </div>    
            </Danger>
        </div>}
        {editingNode.node_type == 'CUSTOM' && (
            <>
                <CustomSQLConfig node={editingNode} onChange={onEditNodeChange}/>
            </>
        )}

        {pipelineNode.data && pipelineNode.data.node_type == 'SOURCE' && (
            <>
                <SourceTableConfig node={editingNode} onChange={onEditNodeChange}/>
            </>
        )}

        {editingNode.node_type == 'STACK' && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'SEED' && (
            <>
                <div className="p-3">
                    This is a read-only seed file.
                </div>
            </>
        )}

        {pipelineNode.data && ['STAGING', 'BUSINESS_OBJECT'].includes(pipelineNode.data.node_type) && (
            <>
                {/* Support for legacy without migration since otherwise we'd have to interpret what each one meant. */}
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    enableMerge
                />
            </>
        )}

        {['MERGE', 'DEDUPLICATE'].includes(editingNode.node_type) && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    mergeTextMode="MERGE"
                    enableMerge
                />
            </>   
        )}
        {editingNode.node_type == 'SUMMARIZE' && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    enableMerge
                    limitToSingleSource
                    mergeTextMode='GROUP_BY'
                />
            </>   
        )}
        {editingNode.node_type == 'IDENTIFY' && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    enableMerge
                    limitToSingleSource
                    mergeTextMode="IDENTIFY"
                    requireMerge
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'DIMENSION' && (
            <>
            
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    enableMerge
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'FACT' && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    enableMerge
                />
            </>   
        )}
        {editingNode.node_type == 'SPLIT' && (
            <>
                <SplitConfig
                    node={editingNode}
                    onChange={onEditNodeChange}
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'DATE_DIMENSION' && (
            <>
                <DateDimConfig
                    node={editingNode}
                    onChange={onEditNodeChange}
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'VIEW' && (
            <>
                <ViewConfig
                    node={editingNode}
                    onChange={onEditNodeChange}
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'DESTINATION' && (
            <>
                <PipelineNodeMappingConfigForm
                    node={editingNode}
                    onChange={onEditNodeChange}
                    limitToSingleSource
                    enableMerge={false}
                />
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'DATAMART' && (
            <>
                <ConfigurationSection>
                    <PipelineNodeBasicConfiguration node={editingNode} onChange={onEditNodeChange}/>

                </ConfigurationSection>
                <ConfigurationSection>
                    <PipelineNodeDataMartConfiguration
                        node={editingNode}
                        onChange={onEditNodeChange}
                    />
                </ConfigurationSection>
                <ConfigurationSection>
                    <PipelineNodeWhitelistConfiguration node={editingNode} onChange={onEditNodeChange}/>

                </ConfigurationSection>
                <ConfigurationSection>
                    <PipelineNodeOutputConfiguration node={editingNode} onChange={onEditNodeChange}/>

                </ConfigurationSection>


                
            </>   
        )}
        {pipelineNode.data && pipelineNode.data.node_type == 'REPORT' && (
            <>
                {/* If the report has measures or dimensions and NOT dimension_ids/measure_ids, show the old form */}
                {((pipelineNode.data.measures && pipelineNode.data.measures.length > 0 && !pipelineNode.data.measure_ids) || (pipelineNode.data.dimensions && pipelineNode.data.dimensions.length > 0 && !pipelineNode.data.dimension_ids)) && <>
                    <PipelineNodeReportEditor
                        node={editingNode}
                        onChange={onEditNodeChange}
                    />
                </>}
                {(pipelineNode.data.measure_ids || pipelineNode.data.dimension_ids || ((!pipelineNode.data.measures || pipelineNode.data.measures.length == 0) && (!pipelineNode.data.dimensions || pipelineNode.data.dimensions.length == 0))) && <>
                    <PipelineNodeReportBuilder
                        node={editingNode}
                        onChange={onEditNodeChange}
                    />
                </>}
                
                
            </>   
        )}
    </div>;

    const [showBuildNodeModal, setShowBuildNodeModal] = useState(false);
    const navigate = useNavigate();

    const optionallySaveThenBuild = useCallback(async () => {
        if (pageDirty) {
            const confirmed = await requireConfirmation('You have unsaved changes which may affect your build.', 'Warning: Unsaved Changes', 'Save & Build', 'Go Back');
            if (!confirmed) {
                return;
            }
            await save();
            setShowBuildNodeModal(true);
        } else {
            setShowBuildNodeModal(true);
        }
    }, [pageDirty, save]);

    if (pipelineNode.status == 'error' && (pipelineNode.error as ApiError).code == 404) {
        return <PipelineNodeNotFound/>
    }

    if (!pipelineNode.data) {
        return <></>;
    }
    return <>
        <BuildNodeModal 
            show={showBuildNodeModal} 
            onClose={() => setShowBuildNodeModal(false)}
            defaultCheck={pipelineNode.data?.node_type == 'SOURCE'}
            defaultUpstream={pipelineNode.data?.node_type != 'SOURCE'}
            header={`Refresh Data: ${pipelineNode.data?.label || ''}`}
            onConfirm={async (selector: "THIS" | "UPSTREAM" | "DOWNSTREAM" | "ALL", force: boolean, check_for_records: boolean) => {

                triggerBuild(pipelineNode.data!, selector, force, check_for_records, () => {
                    navigate(`/node/${pipelineNodeId}`);
                })
            }}
        />
            <div style={{height: isInDraft ? 'calc(100vh - 150px)' : 'calc(100vh - 100px)'}}>
                <Allotment vertical ref={allotmentRef} onChange={allotmentResized}>
                    <Allotment.Pane minSize={200}>
                        
                        {topPane}
                    </Allotment.Pane>
                    <Allotment.Pane snap={false} minSize={30} preferredSize={30}>
                        <DataPaneHeader onClick={() => toggleDataViewer()}>
                            <h2>View Data</h2>
                            {dataViewerIsOpen && <>
                                <button onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowSql(!showSql)
                                }} className="me-3">
                                    {showSql && <i className="mdi mdi-table"/>}
                                    {!showSql && <i className="mdi mdi-code-braces"/>}
                                </button>
                            </>}
                            
                            <button >
                                {dataViewerIsOpen && <i className="mdi mdi-chevron-down"></i>}
                                {!dataViewerIsOpen && <i className="mdi mdi-chevron-up"></i>}
                            </button>
                        </DataPaneHeader>
                        <Pane>
                        {dataViewerIsOpen && <PaneContent>
                            {showSql && <>
                                <Editor
                                    height="100%"
                                    defaultLanguage="sql"
                                    options={{
                                        readOnly: true,
                                        minimap: { enabled: false },
                                    }}
                                    value={editingNode.cached_generated_sql}
                                />
                            </>}
                            {!showSql && <>
                                
                                <PipelineNodeDataTable
                                    pipelineNodeId={pipelineNodeId as string}
                                />
                            </>}
                            
                            </PaneContent> 
                        }
                        </Pane>
                    </Allotment.Pane>
                </Allotment>
            </div>
            
       </>;
                  
                            
                        
}

export default PipelineNodeConfigurationPage;